/**
 * VirtualCharger.app brand mark + wordmark.
 *
 *   <Logo variant="mark" size="md" />              ← square 96×96 favicon-style
 *   <Logo variant="wordmark-horizontal" />          ← mark + wordmark inline
 *   <Logo variant="wordmark-stacked" />             ← mark above wordmark
 *
 * FEAT-017 (Session 8f.11) — optionally renders as a link via `href` +
 * `as='link'`. The in-app surfaces point the logo at `/welcome` so users
 * can jump from the app to the marketing landing; the landing's logo
 * (hand-coded in brand/web/index.html) points back at `/`.
 *
 * The SVG assets are served from `/brand/` (copied at build time from the
 * `brand/visual/` directory in the workspace root).
 */
type Variant = 'mark' | 'wordmark-horizontal' | 'wordmark-stacked';
type Size = 'xs' | 'sm' | 'md' | 'lg';
type As = 'link' | 'static';

interface Props {
  readonly variant?: Variant;
  readonly size?: Size;
  readonly className?: string;
  readonly testId?: string;
  /** Render mode. Defaults to `'link'` when `href` is provided, else `'static'`. */
  readonly as?: As;
  /** Destination URL when rendered as a link. */
  readonly href?: string;
}

const SIZE_PX: Record<Size, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
};

export function Logo({
  variant = 'mark',
  size = 'md',
  className,
  testId,
  as,
  href,
}: Props): JSX.Element {
  const px = SIZE_PX[size];
  const mode: As = as ?? (href !== undefined ? 'link' : 'static');
  const sharedProps = {
    'data-testid': testId !== undefined ? `logo-${testId}` : 'logo',
    'aria-label': 'VirtualCharger',
  };
  const inner = renderInner(variant, px, className, sharedProps);
  if (mode === 'link' && href !== undefined) {
    return (
      <a
        href={href}
        aria-label="VirtualCharger.app home"
        data-testid={testId !== undefined ? `logo-${testId}-link` : 'logo-link'}
        className="inline-flex items-center focus:outline-none focus:ring-2 focus:ring-primary-base rounded"
      >
        {inner}
      </a>
    );
  }
  return inner;
}

function renderInner(
  variant: Variant,
  px: number,
  className: string | undefined,
  sharedProps: { 'data-testid': string; 'aria-label': string },
): JSX.Element {
  if (variant === 'mark') {
    return (
      <img
        src="/brand/logo-mark.svg"
        alt="VirtualCharger"
        width={px}
        height={px}
        className={className}
        {...sharedProps}
      />
    );
  }
  if (variant === 'wordmark-stacked') {
    return (
      <span
        className={`inline-flex flex-col items-center gap-1 ${className ?? ''}`}
        {...sharedProps}
      >
        <img src="/brand/logo-mark.svg" alt="" width={px} height={px} />
        <span className="text-xs font-medium tracking-tight text-text-inverse">
          VirtualCharger.app
        </span>
      </span>
    );
  }
  // wordmark-horizontal
  return (
    <span
      className={`inline-flex items-center gap-2 ${className ?? ''}`}
      {...sharedProps}
    >
      <img src="/brand/logo-mark.svg" alt="" width={px} height={px} />
      <span className="text-base font-semibold tracking-tight text-text-inverse">
        VirtualCharger
      </span>
    </span>
  );
}
